import { Component, Input, OnChanges, OnInit } from "@angular/core";
import { FormGroup, Validators } from "@angular/forms";
import { FormService } from "../../../../../dynamic-forms/form.service";
import { Dropdown } from "../../../../../dynamic-forms/inputs/dropdown/dropdown.model";
import { Radiobutton } from "../../../../../dynamic-forms/inputs/radiobutton/radiobutton.model";
import { SelectableInput } from "../../../../../dynamic-forms/inputs/selectable-input.model";
import { Switch } from "../../../../../dynamic-forms/inputs/switch/switch.model";

@Component({
  selector: "project-config-hedis-setting",
  templateUrl: "./hedis-setting.component.html",
  styleUrls: ["./hedis-setting.component.scss",
              "../../../../../dynamic-forms/inputs/textbox/textbox.component.scss",
              "../project-config.component.scss"],
})
export class HedisSettingComponent implements OnInit, OnChanges {
  @Input() projectAttributes: any;
  measureYearFormGroup: FormGroup;
  measureYear: Dropdown;
  reportingYear: Dropdown;
  autocloseMemberCompliantChase: Radiobutton;
  autocloseMemberCompliantChaseForRecentEventMeasure: Radiobutton;
  nrcRequired: Radiobutton;
  demographicRequired: Radiobutton;
  dataEntryProviderConfirmationEnabled: Switch;
  gapProviderOutreachEnabled: Switch;

  constructor(
    private readonly formService: FormService
  ) { }

  ngOnInit(): void {
    this.createForm();
  }

  ngOnChanges(): void {
    if (this.projectAttributes) {
      this.loadHedisSettings();
    }
  }

  get isRetrievalTypeGap(): boolean {
    return this.projectAttributes && this.projectAttributes.MemberCentricChaseRetrieval.attributeValue === "2";
  }

  private createForm(): void {
    this.measureYear = new Dropdown({
      key: "measureYear",
      label: "Measure Year",
      placeholder: "Select a year",
      options: [
        new SelectableInput({ text: "2025", value: "2025" }),
        new SelectableInput({ text: "2024", value: "2024" }),
        new SelectableInput({ text: "2023", value: "2023" }),
        new SelectableInput({ text: "2022", value: "2022" }),
        new SelectableInput({ text: "2021", value: "2021" }),
      ],
      disabled: true,
      validators: [Validators.required],
      errorMessages: {
        required: "Measure year is required.",
      },
    });
    this.reportingYear = new Dropdown({
      key: "reportingYear",
      placeholder: "Select Reporting Year",
      options: [
        new SelectableInput({ text: "2026", value: "2026" }),
        new SelectableInput({ text: "2025", value: "2025" }),
        new SelectableInput({ text: "2024", value: "2024" }),
        new SelectableInput({ text: "2023", value: "2023" }),
        new SelectableInput({ text: "2022", value: "2022" }),
        new SelectableInput({ text: "2021", value: "2021" }),
      ],
      disabled: true,
      label: "Reporting Year",
      errorMessages: {
        required: "Reporting year is required.",
      },
    });
    this.autocloseMemberCompliantChase = new Radiobutton({
      key: "autocloseMemberCompliantChase",
      label: "Auto-close All Open And Pended Chases When A Member Becomes Compliant",
      options: [
        new SelectableInput({ text: "Yes", value: "1" }),
        new SelectableInput({ text: "No", value: "0" }),
      ],
      tooltip: "When a member/measure becomes compliant based on Admin Data, Compliance Flags (Admin and Supplemental) or Exclusion Flags (Admin and Supplemental) – all member measure chases will auto-close and move to a Delivery Status. If compliance is based on Medical Record Review (MRR) and makes the member/measure compliant and the chase has been through OR1 or OR2 associated chases in status prior to MRR will close. Chases in MRR/OR1/OR2 will need to be completed. These rules apply to all measures excluding (A1c9, BPD, CBP, GSD and HPCMI)",
    });
    this.autocloseMemberCompliantChaseForRecentEventMeasure = new Radiobutton({
      key: "autocloseMemberCompliantChaseForRecentEventMeasure",
      label: "Auto-close All Open And Pended Chases When A Member Becomes Compliant For Most Recent Measure",
      options: [
        new SelectableInput({ text: "Yes", value: "1" }),
        new SelectableInput({ text: "No", value: "0" }),
      ],
      tooltip: "Applicable to (A1c9, BPD, CBP, GSD and HPCMI). When a member/measure becomes compliant based on Admin Data, Compliance Flags (Admin and Supplemental) or Exclusion Flags (Admin and Supplemental) – all member measure chases will auto-close and move to a Delivery Status. If compliance is based on Medical Record Review (MRR) and makes the member/measure compliant and the chase has been through OR1 or OR2 associated chases in status prior to MRR will close. Chases in MRR/OR1/OR2 will need to be completed.",
    });
    this.nrcRequired = new Radiobutton({
      key: "nrcRequired",
      label: "NRC REQUIREMENTS FOR CHASE SUBMISSION",
      options: [
        new SelectableInput({ text: "Required", value: "1" }),
        new SelectableInput({ text: "Not Required", value: "0" }),
      ],
    });
    this.demographicRequired = new Radiobutton({
      key: "demographicRequired",
      label: "DEMOGRAPHIC (RACE & ETHNICITY) REQUIREMENTS FOR CHASE SUBMISSION",
      options: [
        new SelectableInput({ text: "Required", value: "1" }),
        new SelectableInput({ text: "Not Required", value: "0" }),
      ],
    });
    this.dataEntryProviderConfirmationEnabled = new Switch({
      key: "dataEntryProviderConfirmationEnabled",
      label: "Provider Confirmation",
      preceedingOffLabel: "Inactive",
      followingOnLabel: "Active",
      tooltip: "When enabled, Abstractors will have the opportunity to update the associated provider to the medical record.",
    });
    this.gapProviderOutreachEnabled = new Switch({
      key: "gapProviderOutreachEnabled",
      label: "ENABLE GAP PURSUIT CREATION",
      preceedingOffLabel: "Inactive",
      followingOnLabel: "Active",
      tooltip: "This feature allows for gap specific provider outreach on chases that still have non-compliant numerators after overread. Gap Pursuits will automatically be generated on a weekly basis.",
    });

    this.measureYearFormGroup = this.formService.createFormGroup(
      [
        this.measureYear,
        this.reportingYear,
        this.autocloseMemberCompliantChase,
        this.autocloseMemberCompliantChaseForRecentEventMeasure,
        this.nrcRequired,
        this.demographicRequired,
        this.dataEntryProviderConfirmationEnabled,
        this.gapProviderOutreachEnabled]
    );
  }

  private loadHedisSettings(): void {
    this.measureYearFormGroup.patchValue({
      measureYear: this.projectAttributes.MeasureYear.attributeValue,
      reportingYear: this.projectAttributes.ReportingYear.attributeValue,
      autocloseMemberCompliantChase: Number(this.projectAttributes.AutocloseMemberCompliantChase?.attributeValue),
      autocloseMemberCompliantChaseForRecentEventMeasure: Number(this.projectAttributes.AutocloseMemberCompliantChaseForRecentEventMeasure?.attributeValue),
      nrcRequired: Number(this.projectAttributes.NegativeReasonCodeRequired.attributeValue),
      demographicRequired: Number(this.projectAttributes.DemographicRequired.attributeValue),
      dataEntryProviderConfirmationEnabled: Number(this.projectAttributes.DataEntryProviderConfirmationEnabled.attributeValue),
      gapProviderOutreachEnabled: Number(this.projectAttributes.GapProviderOutreachEnabled.attributeValue),
    });
  }

  getFormValue(key: string, type: string = "string"): string | number {
    return type === "string" ? this.measureYearFormGroup.get(key).value : Number(this.measureYearFormGroup.get(key).value);
  }

}
